import { useCallback, useMemo, useState } from 'react';

import { formatName } from '@libs/formatNames';

import { useManagerSocket } from '@features/shared/api/club/hooks';
import { useActiveTournamentParticipantsQuery } from '@features/tournament/api/hooks';
import { getRefereeRoomPath } from '@features/video/lib/ticket';

import { VideoRoom, VideoRoomsModal } from './VideoRoomsModal';

type Props = {
  clubId: string;
  tournamentId: string;
  roomsCount: number;
  onClose: () => void;
};

// Комнаты для судей: состав по номерам комнат участников, страница комнаты в новой вкладке.
// Без номера комнаты участник не получал билет — значит, отсутствует
export function VideoRoomsModalWidget({ clubId, tournamentId, roomsCount, onClose }: Props) {
  // Комнаты, назначенные при открытой модалке. Событие идемпотентно: копим все и накладываем
  // поверх любого снэпшота списка, так не теряется назначение, пришедшее во время запроса.
  // Подписка раньше запроса: между снэпшотом и подпиской окна нет
  const [assigned, setAssigned] = useState<ReadonlyMap<string, number>>(() => new Map());
  useManagerSocket(clubId, (e) => {
    if (e.eventType !== 'ClubParticipantVideoRoomSet' || e.tournamentId !== tournamentId) return;
    const { userId, roomNumber } = e;
    if (roomNumber === undefined) return;
    setAssigned((prev) => new Map(prev).set(userId, roomNumber));
  });

  const { data: participants } = useActiveTournamentParticipantsQuery(
    clubId,
    tournamentId,
    'NumberAsc',
    true,
  );

  const { rooms, absent } = useMemo(() => {
    const active = (participants ?? [])
      .filter((p) => p.status === 'Participating')
      .map((p) => ({
        userId: p.userId,
        name: formatName(p),
        room: assigned.get(p.userId) ?? p.videoRoomNumber,
      }));
    // назначение может прийти раньше, чем карточка узнает о новых комнатах
    const count = Math.max(roomsCount, ...active.map((p) => p.room ?? 0));
    const rooms: VideoRoom[] = Array.from({ length: count }, (_, i) => ({
      number: i + 1,
      participants: active.filter((p) => p.room === i + 1),
    }));
    return { rooms, absent: active.filter((p) => p.room === null) };
  }, [participants, roomsCount, assigned]);

  // Одна вкладка судьи на турнир: повторное открытие перезагружает ее, а не плодит новые;
  // модалка после этого не нужна
  const onOpen = useCallback(
    (roomNumber: number) => {
      window.open(
        getRefereeRoomPath(clubId, { tournamentId, roomNumber }),
        `referee-${tournamentId}`,
      );
      onClose();
    },
    [clubId, tournamentId, onClose],
  );

  return <VideoRoomsModal rooms={rooms} absent={absent} onOpen={onOpen} onClose={onClose} />;
}
